import React from 'react';
import { useNavigate } from 'react-router-dom';

export default function useNavigationGuards({
  allowedPrefixes,
  accessLoaded,
  lastAllowedPath,
  lastDeniedPath,
  fallbackAllowedPath,
  getFullPath,
  onDenied,
  shouldRedirectOnDenied,
}) {
  const navigate = useNavigate();

  // Pre-navigation click interception: block disallowed links before route change
  React.useEffect(() => {
    function onDocumentClick(e) {
      // Only handle unmodified left clicks
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      let el = e.target;
      while (el && el.tagName !== 'A') el = el.parentElement;
      if (!el) return;
      const href = el.getAttribute('href');
      // Allow external, API, and download/file URLs to proceed without SPA guard
      if (
        !href ||
        href.startsWith('http') ||
        href.startsWith('/api/') ||
        href.startsWith('data:') ||
        href.startsWith('blob:') ||
        el.hasAttribute('download')
      )
        return; // external, API, data/blob, or download links
      let targetUrl;
      try {
        // Ignore anchors that are just hash links on same page
        targetUrl = new URL(href, window.location.origin);
      } catch {
        return; // if parsing fails, do not block
      }

      const targetPath = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
      // Determine if target path is allowed
      const lower = (targetUrl.pathname || '').toLowerCase();
      const allowed = allowedPrefixes.some((prefix) => {
        const pl = (prefix || '').toLowerCase();
        return lower === pl || lower.startsWith(pl + '/');
      });
      if (!allowed) {
        e.preventDefault();
        if (lastDeniedPath !== targetPath) {
          onDenied?.(targetPath);
        }
        // Stay on current page; optionally ensure we remain at last allowed
        if (shouldRedirectOnDenied) {
          const dest = lastAllowedPath || sessionStorage.getItem('lastAllowedPath') || fallbackAllowedPath;
          if (dest && getFullPath() !== dest) {
            navigate(dest, { replace: true });
          }
        }
      }
    }
    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, [
    allowedPrefixes,
    lastAllowedPath,
    lastDeniedPath,
    navigate,
    getFullPath,
    fallbackAllowedPath,
    onDenied,
    shouldRedirectOnDenied,
  ]);

  // Intercept programmatic navigations via History API (navigate(), <Link/>, etc.)
  React.useEffect(() => {
    const originalPush = window.history.pushState;
    const originalReplace = window.history.replaceState;
    const isHistoryLibState = (state) => {
      // `history` (used by react-router) stores { usr, key, idx } in history.state
      return !!state && typeof state === 'object' && Object.prototype.hasOwnProperty.call(state, 'idx');
    };
    const notifyLocationChange = () => {
      // If something uses native history APIs directly, React Router won't be notified.
      // Dispatch a popstate so listeners can sync. Avoid double-firing for react-router's own history updates.
      try {
        window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
      } catch {
        // ignore
      }
    };

    const checkAllowed = (urlLike) => {
      try {
        // Allow direct API/file URLs
        if (typeof urlLike === 'string') {
          if (urlLike.startsWith('/api/') || urlLike.startsWith('data:') || urlLike.startsWith('blob:')) return true;
        }
        const targetUrl = new URL(urlLike, window.location.origin);
        const lower = (targetUrl.pathname || '').toLowerCase();
        const allowed = allowedPrefixes.some((prefix) => {
          const pl = (prefix || '').toLowerCase();
          return lower === pl || lower.startsWith(pl + '/');
        });
        return allowed;
      } catch {
        return true; // if parsing fails, do not block
      }
    };

    const reentrant = { current: false };
    const shouldCheckUrl = (url) => typeof url === 'string' && url.length > 0;
    const extractPath = (url) => {
      try {
        const u = new URL(url, window.location.origin);
        return `${u.pathname}${u.search}${u.hash}`;
      } catch {
        return String(url || '');
      }
    };

    window.history.pushState = function (state, title, url) {
      if (reentrant.current) return originalPush.apply(this, arguments);
      if (!shouldCheckUrl(url)) return originalPush.apply(this, arguments);
      if (!accessLoaded) {
        const ret = originalPush.apply(this, arguments);
        if (!isHistoryLibState(state)) notifyLocationChange();
        return ret;
      }
      if (!checkAllowed(url)) {
        onDenied?.(extractPath(url));
        const dest = lastAllowedPath || sessionStorage.getItem('lastAllowedPath') || fallbackAllowedPath;
        if (dest && shouldRedirectOnDenied) {
          reentrant.current = true;
          try {
            navigate(dest, { replace: true });
          } finally {
            reentrant.current = false;
          }
        }
        return;
      }
      const ret = originalPush.apply(this, arguments);
      if (!isHistoryLibState(state)) notifyLocationChange();
      return ret;
    };

    window.history.replaceState = function (state, title, url) {
      if (reentrant.current) return originalReplace.apply(this, arguments);
      if (!shouldCheckUrl(url)) return originalReplace.apply(this, arguments);
      if (!accessLoaded) {
        const ret = originalReplace.apply(this, arguments);
        if (!isHistoryLibState(state)) notifyLocationChange();
        return ret;
      }
      if (!checkAllowed(url)) {
        onDenied?.(extractPath(url));
        const dest = lastAllowedPath || sessionStorage.getItem('lastAllowedPath') || fallbackAllowedPath;
        if (dest && shouldRedirectOnDenied) {
          reentrant.current = true;
          try {
            navigate(dest, { replace: true });
          } finally {
            reentrant.current = false;
          }
        }
        return;
      }
      const ret = originalReplace.apply(this, arguments);
      if (!isHistoryLibState(state)) notifyLocationChange();
      return ret;
    };

    return () => {
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
    };
  }, [allowedPrefixes, accessLoaded, lastAllowedPath, navigate, fallbackAllowedPath, onDenied, shouldRedirectOnDenied]);

  // Global handler for components to trigger access-denied without navigating
  React.useEffect(() => {
    function onAccessDenied(evt) {
      const detail = (evt && evt.detail) || {};
      const target = typeof detail.path === 'string' && detail.path ? detail.path : getFullPath();
      onDenied?.(target);
      const stored = sessionStorage.getItem('lastAllowedPath') || lastAllowedPath || fallbackAllowedPath;
      if (stored && getFullPath() !== stored && shouldRedirectOnDenied) {
        navigate(stored, { replace: true });
      }
    }
    window.addEventListener('moment-access-denied', onAccessDenied);
    return () => window.removeEventListener('moment-access-denied', onAccessDenied);
  }, [fallbackAllowedPath, getFullPath, lastAllowedPath, navigate, onDenied, shouldRedirectOnDenied]);
}


